import React, { useEffect, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import moment from 'moment';

import LockIcon from '@material-ui/icons/Lock';
import { Button } from '@material-ui/core';
import CardMedia from '@material-ui/core/CardMedia';
import Grid from '@material-ui/core/Grid';
import { makeStyles } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';

import * as RewardCardService from '../../api/rewardCardService';
import * as RewardService from '../../api/rewardService';

import Paper from '../Paper';

const useRewardStyles = makeStyles((theme) => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    marginBottom: theme.spacing(),
  },
  cardContainer: {
    display: 'flex',
    flexDirection: 'column',
    padding: 0,
    marginTop: theme.spacing(),
    marginBottom: theme.spacing(3),
    width: 350,
  },
  titleContainer: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(),
  },
  title: {
    fontWeight: theme.typography.fontWeightBold,
    padding: '0 16px',
    textAlign: 'center',
  },
  subRewardTitle: {
    fontWeight: theme.typography.fontWeightBold,
    padding: '0 16px',
  },
  subtitleContainer: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'center',
    marginBottom: theme.spacing(),
  },
  subtitle: {
    color: theme.palette.text.secondary,
    marginRight: 4,
  },
  subtitleBold: {
    color: theme.palette.text.secondary,
    fontWeight: theme.typography.fontWeightBold,
  },
  info: {
    fontWeight: theme.typography.fontWeightRegular,
    marginBottom: theme.spacing(2),
    padding: '0 16px',
  },
  actionContainer: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
  },
  actionButton: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  showDetailContainer: {
    display: 'flex',
    flexDirection: 'row',
    padding: '0 6px',
  },
  showDetailButton: {
    color: theme.palette.text.secondary,
  },
  grow: {
    flexGrow: 1,
  },
  finePrint: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  markdown: {
    marginTop: 8,
    marginBottom: 8,
    fontFamily: 'ClarikaGeometric',
    fontSize: 14,
    fontWeight: 400,
    padding: '0 16px',
  },
  cardMedia: {
    height: 220,
    width: 350,
    borderTopRightRadius: 10,
    borderTopLeftRadius: 10,
    backgroundSize: 'cover',
  },
}));

export function RewardContainer({
  title,
  rewardId,
  handleRedeemReward,
  handleLockedReward,
  locked,
  expiredAt,
  altBusinessName,
  companyName,
}: {
  title: string;
  rewardId: string;
  handleRedeemReward: () => void;
  handleLockedReward: () => void;
  locked: boolean;
  expiredAt: number;
  altBusinessName: string;
  companyName: string;
}) {
  const classes = useRewardStyles();
  const [reward, setReward] = useState<RewardService.Reward | null>(null);
  const [showDetails, setShowDetails] = useState(false);

  useEffect(() => {
    const fetchReward = async () => {
      const res = await RewardService.get(rewardId);
      setReward(res);
    };

    fetchReward();
  }, [rewardId]);

  if (!reward) {
    return null;
  }

  return (
    <div className={classes.root}>
      <Paper className={classes.cardContainer}>
        <CardMedia
          className={classes.cardMedia}
          image={
            reward && reward.images && reward.images[0]
              ? reward.images[0].versions.xl
              : ''
          }
        />
        <div className={classes.titleContainer}>
          <Typography variant="h5" className={classes.title}>
            {title}
          </Typography>
          <div className={classes.subtitleContainer}>
            <Typography
              variant="body2"
              className={classes.subtitle}
              gutterBottom
            >
              by
            </Typography>
            <Typography
              variant="body2"
              className={classes.subtitleBold}
              gutterBottom
            >
              {altBusinessName ? altBusinessName : companyName}
            </Typography>
          </div>
        </div>
        <div>
          <Typography
            variant="body2"
            className={classes.subRewardTitle}
            gutterBottom
          >
            What You'll Get
          </Typography>
          <ReactMarkdown className={classes.markdown}>
            {reward.what}
          </ReactMarkdown>
        </div>
        {!showDetails && (
          <>
            <Typography
              variant="body2"
              className={classes.subRewardTitle}
              gutterBottom
            >
              Valid Until
            </Typography>
            <div>
              <Typography variant="body2" className={classes.info} gutterBottom>
                {moment.unix(expiredAt).format('MMM DD, YYYY')}
              </Typography>
            </div>
          </>
        )}
        {showDetails && (
          <div className={classes.finePrint}>
            <Typography
              variant="body2"
              className={classes.subRewardTitle}
              gutterBottom
            >
              The Fine Print
            </Typography>
            <div>
              <ReactMarkdown className={classes.markdown}>
                {reward.info}
              </ReactMarkdown>
            </div>
            <Typography
              variant="body2"
              className={classes.subRewardTitle}
              gutterBottom
            >
              Valid Until
            </Typography>
            <div>
              <Typography variant="body2" className={classes.info} gutterBottom>
                {moment.unix(expiredAt).format('MMM DD, YYYY')}
              </Typography>
            </div>
          </div>
        )}
        <div className={classes.showDetailContainer}>
          <Button
            variant="text"
            onClick={() => setShowDetails(!showDetails)}
            className={classes.showDetailButton}
          >
            {showDetails ? 'Hide' : 'Expand'}
          </Button>
          <div className={classes.grow} />
        </div>
        <div className={classes.actionContainer}>
          <Button
            color="primary"
            className={classes.actionButton}
            startIcon={locked ? <LockIcon fontSize="small" /> : null}
            onClick={locked ? handleLockedReward : handleRedeemReward}
          >
            {locked ? 'Unlock' : 'Redeem'}
          </Button>
        </div>
      </Paper>
    </div>
  );
}

const useStyles = makeStyles((theme) => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
  },
}));

type Props = {
  rewardCardList: RewardCardService.RewardCard[];
  handleRedeemReward: (rewardCardId: string, preferredStoreId?: string) => void;
  handleLockedReward: (surveyId: string, rewardCardId: string) => void;
};

export default function RewardCard({
  rewardCardList,
  handleRedeemReward,
  handleLockedReward,
}: Props) {
  const classes = useStyles();

  return (
    <Grid item xs={12} className={classes.root}>
      {rewardCardList.map((rewardCard, idx) => (
        <RewardContainer
          key={idx}
          title={rewardCard.title}
          rewardId={rewardCard.reward.id}
          locked={rewardCard.locked}
          handleRedeemReward={() => {
            handleRedeemReward(
              rewardCard.id,
              rewardCard.storeID ? rewardCard.storeID : undefined,
            );
          }}
          handleLockedReward={() => {
            handleLockedReward(rewardCard.survey.id, rewardCard.id);
          }}
          expiredAt={rewardCard.expiresAt}
          altBusinessName={rewardCard.altBusinessName}
          companyName={rewardCard.merchantName}
        />
      ))}
    </Grid>
  );
}
